/**
 * Order funnel: shared enums and cart line shapes.
 *
 * @remarks Safe to import from server components (`page.tsx`) and client code — no `'use client'`.
 * Runtime enum values must stay available on the server for URL / search-param parsing.
 */

/**
 * Kind of product line in the order cart; drives API payload shape and step-specific UI (e.g. insurance, calendar).
 */
export enum CartProductType {
	/** Doctor appointment: workplace template + concrete timeslot. */
	TEMPLATE = 'template',
	/** Ambulance flow: single price-list row without slot selection. */
	PRICE_LIST_ITEM = 'price-list-item',
	/** One annual program line; optional extra modules. */
	ANNUAL_PROGRAM = 'annual-program',
	/** Family bundle: multiple program lines with shared discount rules. */
	ANNUAL_PROGRAM_FAMILY = 'annual-program-family',
	PREVENTIVE_EXAM = 'preventive-examination',
	CREDIT_PROGRAM = 'credit-program',
	PROMOTION = 'promotion'
}

/**
 * One cart line: discriminated by {@link CartProductType}; `data` carries ids (and optional module ids) for the API.
 */
export type CartProduct =
	| {
			type: CartProductType.TEMPLATE
			data: { templateId: number; timeslotId: number }
	  }
	| { type: CartProductType.PRICE_LIST_ITEM; data: { itemId: number } }
	| {
			type: CartProductType.ANNUAL_PROGRAM
			data: { programId: number; extraModuleIds?: number[] }
	  }
	| {
			type: CartProductType.ANNUAL_PROGRAM_FAMILY
			data: { programId: number; extraModuleIds?: number[] }
	  }
	| {
			type: CartProductType.PREVENTIVE_EXAM
			data: { programId: number; extraModuleIds?: number[] }
	  }
	| { type: CartProductType.CREDIT_PROGRAM; data: { programId: number } }
	| { type: CartProductType.PROMOTION; data: { programId: number } }

/**
 * How the user entered the flow — affects progress bar and whether step 1 (ambulance) is skippable.
 */
export enum OrderFlowMode {
	/** User picks ambulance, then product, then patient details. */
	STANDARD = 'standard',
	/** Pre-filled from URL (e.g. deep link): cart and sometimes ambulance may be initialised without step 1. */
	DIRECT = 'direct'
}
